import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {InvitationPreview} from "../../models/invitationResponse";
import {InvitationService} from "../../services/invitation/invitation.service";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";

import {AppEventsService} from "../../services/app-events/app-events.service";
import {HouseholdService} from "../../services/property/household.service";
import {Household, HouseholdWithStores} from "../../models/household";
import {UiAlertComponent, UiBadgeComponent} from "../../shared/ui";
import {PageHeaderService} from "../../services/page-header/page-header.service";
import {AppLogger, AppLoggerService} from "../../core/logging";

@Component({
  selector: 'app-invitation-finalize',
  standalone: true,
  imports: [
    UiAlertComponent,
    UiBadgeComponent
  ],
  templateUrl: './invitation-finalize.component.html',
  styleUrl: './invitation-finalize.component.scss'
})
export class InvitationFinalizeComponent implements OnInit {

  readonly log : AppLogger;

  isValid = true;           // is the query valid (has token/decision)?
  isLoading = false;        // loading or submitting
  invitationToken: string | null = null;
  decision: 'accept' | 'reject' | null = null;
  householdsLoaded = false;
  households: HouseholdWithStores[] = [];

  preview: InvitationPreview | null = null;

  // result state
  finalized = false;                        // we attempted the action
  finalizedDecision: 'accept' | 'reject' | null = null;
  alreadyProcessed = false;                 // server says accepted/rejected already
  errorMsg: string | null = null;           // fatal error to show

  selectedHouseholdId: number | string | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationService: InvitationService,
    private householdService: HouseholdService,
    private appEvents: AppEventsService,
    private appLogger: AppLoggerService,
    private pageHeader: PageHeaderService
  ) {
    this.log = this.appLogger.createForClass(InvitationFinalizeComponent);
  }

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    const decision = this.route.snapshot.queryParamMap.get('decision');

    if (!token || (decision !== 'accept' && decision !== 'reject')) {
      this.isValid = false;
      this.errorMsg = 'Invalid invitation link.';
      return;
    }

    this.invitationToken = token;
    this.decision = decision;

    this.loadAndFinalize(token, decision);
  }

  private loadAndFinalize(token: string, decision: 'accept' | 'reject'): void {
    this.isLoading = true;
    this.invitationService.getInvitationPreview(token).subscribe({
      next: (dto) => {
        this.preview = dto;

        this.log.debug("Invitation preview loaded: ", {
          invitationId: dto.invitation_id,
          householdName: dto.household_name,
          valid: dto.valid
        });

        this.pageHeader.setPageTitle("Invitation to join " + dto.household_name);

        // 1) still valid?
        if (!dto.valid) {
          this.isLoading = false;
          this.alreadyProcessed = true;   // treat as not actionable
          this.errorMsg = 'This invitation is no longer available.';
          return;
        }

        // 2/3) perform decision (idempotent server should return 409 if already accepted/rejected)
        this.perform(decision, dto.invitation_id);
      },
      error: (e: HttpErrorResponse) => {
        this.isLoading = false;
        this.isValid = false;
        this.errorMsg = e.error?.message ?? 'Failed to load invitation.';
      }
    });
  }

  private perform(decision: 'accept' | 'reject', invitationId: number): void {
    const call = decision === 'accept'
      ? this.invitationService.acceptInvitation(invitationId)
      : this.invitationService.rejectInvitation(invitationId);

    call.subscribe({
      next: () => {
        this.isLoading = false;
        this.finalized = true;
        this.finalizedDecision = decision;

        if (this.finalizedDecision === 'accept') {
          this.loadHouseholds();
        }

      },
      error: (e: HttpErrorResponse) => {
        this.isLoading = false;

        if (e.status === HttpStatusCode.Conflict) {
          // already accepted/rejected on the server
          this.alreadyProcessed = true;
          this.finalized = true;
          this.finalizedDecision = null;
          return;
        }

        if (e.status === HttpStatusCode.Forbidden) {
          this.alreadyProcessed = true;
          this.finalized = true;
          this.finalizedDecision = null;
          this.errorMsg = 'You are not allowed to perform this action. Did you use the same email address as invited?';
          return;
        }

        this.errorMsg = e.error?.message ?? 'Failed to finalize invitation.';
      }
    });
  }

  goHome(): void {
    this.router.navigate(['/home']);
  }

  loadHouseholds(): void {
    this.householdService.households().subscribe((households) => {
      this.households = households;
      this.householdsLoaded = true;
    });
  }

  selectHousehold(h: HouseholdWithStores) {
    this.selectedHouseholdId = h.id;

    const update: Household = {
      id: h.id,
      name: h.name,
      is_default: true
    }

    this.householdService.update(h.id, update).subscribe(() => {
      this.appEvents.notifyInvitationChanged();
      this.goHome();
    });
  }


}
